import { useCallback, useEffect, useRef, useState } from 'react';
import Card from '@mui/material/Card';
import Tooltip from '@mui/material/Tooltip';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FolderZipOutlinedIcon from '@mui/icons-material/FolderZipOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

import SoftBox from 'components/SoftBox';
import SoftButton from 'components/SoftButton';
import SoftTypography from 'components/SoftTypography';

import { useAuth } from 'auth';
import {
  allItems,
  coverImageUrl,
  fetchGallery,
  galleryErrorMessage,
  isDesignWorkPending,
  markGallerySelectionSeenRequest,
  readCachedGallery,
  type Gallery,
} from 'gallery';

import { GalleryDesign } from './gallery-design';
import { GalleryFiles } from './gallery-files';
import { GallerySettingsDialog } from './gallery-settings-dialog';
import { GalleryShareCard } from './gallery-share-card';
import {
  formatShootDate,
  notifyGalleryError,
  notifyGallerySuccess,
  useGalleryText,
} from './gallery-ui';

// While items are PROCESSING (thumbnails being made) the page asks again, for at most ~10 min.
const PROCESSING_POLL_MS = 5000;
const PROCESSING_POLL_LIMIT = 120;
const UPLOAD_RELOAD_MS = 1500;

export type GalleryAction = (
  action: () => Promise<Gallery | void>,
  options?: { success?: string }
) => Promise<boolean>;

export type GalleryPageMode = 'files' | 'design';

interface GalleryPageProps {
  galleryUuid: string;
  initialView: 'all' | 'selection';
  // Files (cover, share card, files) or the Design view; it lives in the URL like the gallery.
  mode?: GalleryPageMode;
  onModeChange?: (mode: GalleryPageMode) => void;
  onDeleted: () => void;
}

export function GalleryPage({
  galleryUuid,
  initialView,
  mode = 'files',
  onModeChange,
  onDeleted,
}: GalleryPageProps) {
  const { accessToken } = useAuth();
  const token = accessToken ?? undefined;
  const { locale, t, plural } = useGalleryText();
  const [gallery, setGallery] = useState<Gallery | undefined>(() => readCachedGallery(galleryUuid));
  const [isMissing, setIsMissing] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const isMounted = useRef(true);
  const hasGallery = useRef(Boolean(gallery));
  const reloadTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const polls = useRef(0);

  useEffect(
    () => () => {
      isMounted.current = false;
      if (reloadTimer.current) {
        clearTimeout(reloadTimer.current);
      }
    },
    []
  );

  const reload = useCallback(async () => {
    try {
      const next = await fetchGallery(galleryUuid, token);
      if (isMounted.current) {
        hasGallery.current = true;
        setGallery(next);
      }
    } catch {
      if (!isMounted.current) {
        return;
      }
      if (hasGallery.current) {
        notifyGalleryError(t.title, t.loadFailed);
      } else {
        setIsMissing(true);
      }
    }
  }, [galleryUuid, t.loadFailed, t.title, token]);

  // Many uploads finishing close together make one request.
  const scheduleReload = useCallback(() => {
    if (reloadTimer.current) {
      return;
    }
    reloadTimer.current = setTimeout(() => {
      reloadTimer.current = null;
      void reload();
    }, UPLOAD_RELOAD_MS);
  }, [reload]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // The Design view also follows an uploaded cover being made and the watermarked copies.
  const hasProcessing = gallery
    ? allItems(gallery).some((item) => item.state === 'PROCESSING') ||
      (mode === 'design' && isDesignWorkPending(gallery))
    : false;
  useEffect(() => {
    if (!hasProcessing || polls.current >= PROCESSING_POLL_LIMIT) {
      return undefined;
    }
    const timer = setTimeout(() => {
      polls.current += 1;
      void reload();
    }, PROCESSING_POLL_MS);
    return () => clearTimeout(timer);
  }, [gallery, hasProcessing, reload]);

  // Opening a gallery with a new selection marks that sending seen on the server, so its banner
  // and the app's drawer mark go away on every device. Once per sending, even if it fails.
  const unseenSending =
    gallery?.hasNewSelection && gallery.selection.sentAt
      ? `${gallery.uuid}@${gallery.selection.sentAt}`
      : null;
  const markedSending = useRef<string | null>(null);
  useEffect(() => {
    if (!unseenSending || markedSending.current === unseenSending) {
      return;
    }
    markedSending.current = unseenSending;
    markGallerySelectionSeenRequest(galleryUuid, token)
      .then(() => {
        if (isMounted.current) {
          setGallery((current) => (current ? { ...current, hasNewSelection: false } : current));
        }
      })
      .catch(() => undefined);
  }, [galleryUuid, token, unseenSending]);

  const run: GalleryAction = useCallback(
    async (action, options) => {
      try {
        const result = await action();
        if (!isMounted.current) {
          return true;
        }
        if (result) {
          setGallery(result);
        } else {
          await reload();
        }
        if (options?.success) {
          notifyGallerySuccess(t.title, options.success);
        }
        return true;
      } catch (error) {
        notifyGalleryError(t.title, galleryErrorMessage(error, t.errors));
        return false;
      }
    },
    [reload, t.errors, t.title]
  );

  if (!gallery) {
    return (
      <SoftTypography variant="button" color="text" data-testid="gallery-page-status">
        {isMissing ? t.notFound : t.loading}
      </SoftTypography>
    );
  }

  const cover = coverImageUrl(gallery);
  const date = formatShootDate(gallery.shootDate, locale) ?? t.noDate;
  const activity = [
    {
      key: 'views',
      icon: <VisibilityOutlinedIcon />,
      value: gallery.counters.views,
      label: t.counters.views,
    },
    {
      key: 'downloads',
      icon: <FileDownloadOutlinedIcon />,
      value: gallery.counters.downloads,
      label: t.counters.downloads,
    },
    {
      key: 'zips',
      icon: <FolderZipOutlinedIcon />,
      value: gallery.counters.zips,
      label: t.counters.zips,
    },
    {
      key: 'favorites',
      icon: <FavoriteBorderIcon />,
      value: gallery.counters.favorites,
      label: t.counters.favorites,
    },
  ];

  const settingsDialog = isSettingsOpen ? (
    <GallerySettingsDialog
      gallery={gallery}
      token={token}
      run={run}
      onClose={() => setIsSettingsOpen(false)}
      onDeleted={onDeleted}
    />
  ) : null;

  if (mode === 'design') {
    return (
      <SoftBox display="flex" flexDirection="column" gap={2} data-testid="gallery-page">
        <Card sx={{ px: 3, py: 2 }}>
          <SoftBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <SoftBox flex={1} minWidth={0}>
              <SoftTypography variant="h5" data-testid="gallery-title">
                {gallery.title.trim() || t.untitled}
              </SoftTypography>
              <SoftTypography variant="caption" color="text">
                {`${date} · ${plural(t.fileCount, gallery.itemCount)}`}
              </SoftTypography>
            </SoftBox>
            <SoftButton
              variant="outlined"
              color="info"
              size="small"
              onClick={() => onModeChange?.('files')}
              startIcon={<PhotoLibraryOutlinedIcon />}
              data-testid="gallery-design-back"
            >
              {t.design.back}
            </SoftButton>
            <SoftButton
              variant="outlined"
              color="info"
              size="small"
              onClick={() => setIsSettingsOpen(true)}
              startIcon={<SettingsOutlinedIcon />}
              data-testid="gallery-settings-open"
            >
              {t.settingsButton}
            </SoftButton>
          </SoftBox>
        </Card>
        <GalleryDesign gallery={gallery} token={token} run={run} />
        {settingsDialog}
      </SoftBox>
    );
  }

  return (
    <SoftBox display="flex" flexDirection="column" gap={2} data-testid="gallery-page">
      {/* The app's cover: title, date and file count bottom-left, guest activity as an icon column
          on the right, Design and Settings on the cover, so no header row repeats the title. */}
      <Card
        sx={{ position: 'relative', overflow: 'hidden', height: 300 }}
        data-testid="gallery-cover"
      >
        {cover ? (
          <img
            src={cover}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <SoftBox height="100%" bgcolor="grey.400" />
        )}
        <SoftBox
          position="absolute"
          sx={{ inset: 0, background: 'linear-gradient(transparent 45%, rgba(0,0,0,.6))' }}
        />
        <SoftBox position="absolute" top={12} right={12} display="flex" gap={1}>
          {onModeChange ? (
            <SoftButton
              variant="contained"
              onClick={() => onModeChange('design')}
              startIcon={<PaletteOutlinedIcon />}
              data-testid="gallery-design-open"
              sx={coverButtonSx}
            >
              {t.design.open}
            </SoftButton>
          ) : null}
          <SoftButton
            variant="contained"
            onClick={() => setIsSettingsOpen(true)}
            startIcon={<SettingsOutlinedIcon />}
            data-testid="gallery-settings-open"
            sx={coverButtonSx}
          >
            {t.settingsButton}
          </SoftButton>
        </SoftBox>
        <SoftBox position="absolute" left={24} bottom={20} right={96}>
          <SoftTypography
            variant="h3"
            sx={{ color: '#fff !important' }}
            data-testid="gallery-title"
          >
            {gallery.title.trim() || t.untitled}
          </SoftTypography>
          <SoftTypography variant="body2" sx={{ color: 'rgba(255,255,255,.85) !important' }}>
            {`${date} · ${plural(t.fileCount, gallery.itemCount)}`}
          </SoftTypography>
        </SoftBox>
        <SoftBox
          position="absolute"
          right={20}
          bottom={20}
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={1.5}
          data-testid="gallery-activity"
        >
          {activity.map((entry) => (
            <Tooltip key={entry.key} title={entry.label} placement="left">
              <SoftBox
                textAlign="center"
                sx={{ color: '#fff', textShadow: '0 1px 4px rgba(0,0,0,.6)' }}
                aria-label={`${entry.label}: ${entry.value}`}
                data-testid={`gallery-activity-${entry.key}`}
              >
                <SoftBox lineHeight={1} sx={{ '& svg': { fontSize: 22 } }}>
                  {entry.icon}
                </SoftBox>
                <SoftBox fontSize={13} fontWeight={700}>
                  {entry.value}
                </SoftBox>
              </SoftBox>
            </Tooltip>
          ))}
        </SoftBox>
      </Card>
      <GalleryShareCard gallery={gallery} token={token} run={run} />
      <GalleryFiles
        gallery={gallery}
        token={token}
        run={run}
        setGallery={setGallery}
        reload={reload}
        scheduleReload={scheduleReload}
        initialView={initialView}
      />
      {settingsDialog}
    </SoftBox>
  );
}

const coverButtonSx = {
  bgcolor: 'rgba(0,0,0,.55) !important',
  color: '#fff !important',
  boxShadow: 'none',
};
